'use client';

import { MeterReaderView } from '@slims/ui/organisms';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { Meter } from '@/hooks/use-meters';
import { type MeterProviderId, PROVIDER_DEFINITIONS } from '@slims/shared/providers';
import { api, ApiError } from '@/lib/utils/api';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

type ReaderMode = 'none' | 'gateway' | MeterProviderId;

const CLOUD_PROVIDERS: MeterProviderId[] = ['iometer2g', 'smartgw'];

const MODE_OPTIONS = [
	{ value: 'none', label: 'Nessun lettore collegato' },
	{ value: 'gateway', label: 'Gateway hardware (push)' },
	...PROVIDER_DEFINITIONS.map((d) => ({ value: d.id, label: d.label })),
];

interface ConnectionDTO {
	provider: string;
	config: Record<string, string>;
	enabled: boolean;
	status: string | null;
	lastSyncAt: string | null;
	lastError: string | null;
}

interface MeterReaderSectionProps {
	meter: Meter;
	onUpdate: (id: string, patch: Record<string, unknown>) => Promise<unknown>;
	onOpenGateway: (meter: Meter) => void;
}

/** Credenziali e collegamenti del lettore, separati dal profilo del punto. */
export function MeterReaderSection({ meter, onUpdate, onOpenGateway }: MeterReaderSectionProps) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [error, setError] = useState<string | null>(null);
	const orgId = useAdminScopeStore((s) => s.configId);
	const [mode, setMode] = useState<ReaderMode>('none');
	const [conn, setConn] = useState<ConnectionDTO | null>(null);
	const [config, setConfig] = useState<Record<string, string>>({});
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [testMsg, setTestMsg] = useState<{ ok: boolean; message: string } | null>(null);
	const [importing, setImporting] = useState(false);

	useEffect(() => {
		let active = true;
		setLoading(true);
		setError(null);
		setConn(null);
		setConfig({});
		api
			.get<ConnectionDTO | null>(`/api/meters/${meter.id}/connection?orgId=${orgId}`)
			.then((c) => {
				if (!active) return;
				if (c && CLOUD_PROVIDERS.includes(c.provider as MeterProviderId)) {
					setConn(c);
					setMode(c.provider as ReaderMode);
					setConfig({ ...c.config });
				} else if (meter.gatewayEnabled) {
					setMode('gateway');
				} else {
					setMode('none');
				}
			})
			.catch(() => {
				if (active) setError('Impossibile caricare il lettore. Ricarica la pagina per riprovare.');
			})
			.finally(() => active && setLoading(false));
		return () => {
			active = false;
		};
	}, [meter.id, meter.gatewayEnabled, orgId]);

	const def = PROVIDER_DEFINITIONS.find((d) => d.id === mode);
	const set = (k: string, v: string) => setConfig((c) => ({ ...c, [k]: v }));

	// Selecting a mode is local only — nothing is persisted or attempted until
	// the user clicks "Salva lettore".
	function handleModeChange(next: ReaderMode) {
		setMode(next);
		setTestMsg(null);
		if (next !== 'none' && next !== 'gateway') {
			setConfig(conn?.provider === next ? { ...conn.config } : {});
		}
	}

	async function handleTest() {
		if (!def) return;
		setBusy(true);
		setTestMsg(null);
		try {
			const r = await api.post<{ ok: boolean; message: string }>(
				`/api/meters/${meter.id}/connection/test?orgId=${orgId}`,
				{ provider: mode, config },
			);
			setTestMsg(r);
		} catch (e) {
			setTestMsg({ ok: false, message: e instanceof Error ? e.message : 'Test fallito' });
		} finally {
			setBusy(false);
		}
	}

	// Backfill the load curve into the database over the provider's max range.
	// No body → the API pulls the widest window it allows and upserts into
	// energy_readings, which the hourly curve reads back.
	async function handleImportHistory() {
		setImporting(true);
		try {
			const r = await api.post<{ fetched: number; written: number; skippedExisting: number }>(
				`/api/meters/${meter.id}/connection/import-history?orgId=${orgId}`,
				{},
			);
			toast.success(
				`Storico importato: ${r.written} letture (${r.fetched} campioni, ${r.skippedExisting} già presenti)`,
			);
			// Import clears the connection error + stamps the sync server-side;
			// refetch so "Mai sincronizzato · ⚠ …" updates without a reload.
			const fresh = await api
				.get<ConnectionDTO | null>(`/api/meters/${meter.id}/connection?orgId=${orgId}`)
				.catch(() => null);
			if (fresh) setConn(fresh);
			void queryClient.invalidateQueries({ queryKey: ['meter-hourly', orgId, meter.id] });
		} catch (e) {
			toast.error(e instanceof Error ? e.message : 'Import storico fallito');
		} finally {
			setImporting(false);
		}
	}

	// Persist the selected reader. Saves the configured value only — it does NOT
	// test/connect (cloud providers are rate-limited; use "Verifica" for that).
	async function handleSave() {
		setBusy(true);
		try {
			if (mode === 'none') {
				await api.delete(`/api/meters/${meter.id}/connection?orgId=${orgId}`).catch((e) => {
					if (!(e instanceof ApiError && e.status === 404)) throw e;
				});
				setConn(null);
				await onUpdate(meter.id, { telemetryMode: 'simulation', gatewayEnabled: false });
			} else if (mode === 'gateway') {
				await api.delete(`/api/meters/${meter.id}/connection?orgId=${orgId}`).catch((e) => {
					if (!(e instanceof ApiError && e.status === 404)) throw e;
				});
				setConn(null);
				await onUpdate(meter.id, { telemetryMode: 'realtime' });
			} else if (def) {
				const missing = def.configFields.filter((f) => f.required && !config[f.key]?.trim());
				if (missing.length) {
					toast.error(`Campi mancanti: ${missing.map((f) => f.label).join(', ')}`);
					return;
				}
				const saved = await api.put<ConnectionDTO>(
					`/api/meters/${meter.id}/connection?orgId=${orgId}`,
					{
						provider: mode,
						config,
						enabled: true,
					},
				);
				setConn(saved);
				setConfig({ ...saved.config });
				await onUpdate(meter.id, { telemetryMode: 'realtime' });
			}
			toast.success('Lettore salvato');
		} catch (e) {
			toast.error(e instanceof Error ? e.message : 'Salvataggio fallito');
		} finally {
			setBusy(false);
		}
	}

	return (
		<MeterReaderView
			mode={mode}
			options={MODE_OPTIONS}
			fields={def?.configFields}
			config={config}
			loading={loading}
			error={error}
			busy={busy}
			importing={importing}
			canImport={!!def?.supportsHistory && conn?.provider === mode}
			gatewayEnabled={!!meter.gatewayEnabled}
			lastSyncAt={conn?.provider === mode ? conn.lastSyncAt : null}
			lastError={conn?.provider === mode ? conn.lastError : null}
			testMessage={testMsg}
			onModeChange={(next) => handleModeChange(next as ReaderMode)}
			onFieldChange={set}
			onSave={handleSave}
			onTest={handleTest}
			onImport={handleImportHistory}
			onOpenGateway={() => onOpenGateway(meter)}
			onOpenMqtt={() => navigate(`/configurations/${orgId}/details`)}
		/>
	);
}
